import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '../services/auth.service';

/**
 * Requires the user be signed in AND have at least one verified contact method - a verified phone
 * (Firebase Phone Auth only ever sets `user.phoneNumber` after a successful OTP verification, so
 * its mere presence already means verified - there's no separate phoneVerified field on the
 * Firebase User object to check) or a verified email (`user.email && user.emailVerified`).
 *
 * This is effectively always true for any account created through this app's UI today - the only
 * two sign-in methods offered are Google (always emailVerified: true, automatically) and Mobile
 * OTP (always sets phoneNumber, inherently verified) - but it's still the right rule to encode
 * explicitly rather than dropping to a bare auth check: it states the real intended business rule
 * in code, and defends against a future auth method silently bypassing it. See CLAUDE.md.
 */
export const checkoutGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const router = inject(Router);

  // On a fresh page load, Firebase's persisted session takes a moment to rehydrate from storage —
  // `currentUser()` can still be at its initial `null` here even for an actually-logged-in user.
  // authStateReady() resolves once that initial resolution is done, so the guard doesn't wrongly
  // redirect a real session to /login just because it ran before onAuthStateChanged first fired.
  await auth.authStateReady();

  const user = authService.currentUser();

  if (!user) {
    return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
  }

  const hasVerifiedContactMethod = !!user.phoneNumber || (!!user.email && user.emailVerified);
  if (!hasVerifiedContactMethod) {
    // Unreachable today (see doc-comment above) - if it's ever hit, /profile is where a contact
    // method actually gets added/verified, not a resurrected modal for a case that can't occur.
    return router.createUrlTree(['/profile'], { queryParams: { returnUrl: state.url } });
  }

  return true;
};
